/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller } from 'react-hook-form';
import { Skeleton } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  FormCancelButton,
  Inputs,
  InputPhone,
  InputSkeleton,
  TimezoneSelector,
  getFieldProps,
  ChipSelectList,
  ChipOption,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { ViewProps } from './types';
import { validatePhone } from '../../../../utils/helpers/validation';

const roleOptions: ChipOption[] = [
  { key: RoleName.ADMIN, label: 'Admin', value: RoleName.ADMIN },
  { key: RoleName.CARE, label: 'Care', value: RoleName.CARE },
  { key: RoleName.DATA, label: 'Data', value: RoleName.DATA },
  { key: RoleName.MARKETING, label: 'Marketing', value: RoleName.MARKETING },
  { key: RoleName.PARTNERSHIPS, label: 'Partnerships', value: RoleName.PARTNERSHIPS },
  { key: RoleName.PHOTO, label: 'Photo', value: RoleName.PHOTO },
];

const EditUserFormView = ({
  control,
  defaultSelectedRoles,
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  onCancel,
  onSubmit,
  open,
  register,
  submissionError,
  teamMember,
}: ViewProps) => (
  <FormDialog
    open={open}
  >
    <FormDialogTitle handleClose={onCancel}>
      {teamMember?.fullName
        ? `Edit ${teamMember?.fullName}`
        : <Skeleton variant="rounded" />}
    </FormDialogTitle>
    <FormElement name="profile-editor-form" onSubmit={handleSubmit(onSubmit)}>
      <FormDialogContent>
        {!teamMember ? (
          <FormInputsStack>
            <InputSkeleton key="givenName" />
            <InputSkeleton key="familyName" />
            <InputSkeleton key="email" />
            <InputSkeleton key="phone" />
            <InputSkeleton key="timeZone" />
          </FormInputsStack>
        ) : (
          <FormInputsStack>
            {submissionError
              && (
                <FormSubmissionError
                  message={submissionError.message}
                  errorCode={submissionError.code}
                  errorId={submissionError.id}
                />
              )}
            <Inputs.Text
              defaultValue={teamMember?.givenName}
              error={!!errors?.givenName}
              helperText={errors?.givenName?.message as string}
              id="givenName"
              label="First Name"
              required
              {...register('givenName', { required: 'Please enter a first name' })}
            />
            <Inputs.Text
              defaultValue={teamMember?.familyName}
              error={!!errors?.familyName}
              helperText={errors?.familyName?.message as string}
              id="familyName"
              label="Last Name"
              required
              {...register('familyName', { required: 'Please enter a last name' })}
            />
            <Controller
              control={control}
              defaultValue={teamMember?.phone}
              name="phone"
              render={({ field }) => (
                <InputPhone
                  error={!!errors.phone}
                  id="phone"
                  helperText={errors.phone ? 'Please enter a valid number.' : ''}
                  label="Phone"
                  {...field}
                />
              )}
              rules={{ validate: validatePhone }}
            />
            <Controller
              control={control}
              defaultValue={teamMember?.timeZone}
              name="timeZone"
              render={({ field }) => (
                <TimezoneSelector
                  id="timeZone"
                  label="Timezone"
                  {...getFieldProps('timeZone', errors)}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              defaultValue={defaultSelectedRoles}
              name="roles"
              rules={{
                validate: ((value) => value.length > 0) || 'Please select at least one role',
              }}
              render={({ field }) => (
                <ChipSelectList
                  chipProps={errors?.roles ? { sx: { borderColor: 'error.dark', color: 'error.dark' } } : undefined}
                  label="Roles"
                  labelTypographyProps={errors?.roles ? { color: 'error.dark' } : undefined}
                  chipOptions={roleOptions}
                  selectedChips={field.value}
                  onChange={(selectedChips) => {
                    field.onChange(selectedChips);
                  }}
                />
              )}
            />
          </FormInputsStack>
        )}
      </FormDialogContent>
      <FormDialogActions>
        <FormCancelButton onClick={onCancel} />
        <LoadingButton
          variant="contained"
          type="submit"
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Submit
        </LoadingButton>
      </FormDialogActions>
    </FormElement>
  </FormDialog>
);

export default EditUserFormView;
